import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cancelWindowAnimationFrame, requestWindowAnimationFrame } from '@/lib/utils/window-timers';
import type { Message } from '../chat-types';

type BlurRegion = { top: number; height: number } | null;

type VisibleMessageFilter = (message: Message) => boolean;

const defaultVisibility: VisibleMessageFilter = (message) =>
  !(message.role === 'system' && message.id.startsWith('context-'));

interface UseChatBlurOverlayOptions {
  filterVisibleMessages?: VisibleMessageFilter;
  messages: Message[];
}

interface UseChatBlurOverlayResult {
  blurRegion: BlurRegion;
  registerMessageElement: (id: string) => (node: HTMLDivElement | null) => void;
  registerWrapper: (node: HTMLDivElement | null) => void;
}

export const useChatBlurOverlay = ({
  messages,
  filterVisibleMessages = defaultVisibility,
}: UseChatBlurOverlayOptions): UseChatBlurOverlayResult => {
  const wrapperReference = useRef<HTMLDivElement | null>(null);
  const messageElementsReference = useRef(new Map<string, HTMLDivElement>());
  const messagesReference = useRef<Message[]>(messages);
  const [blurRegion, setBlurRegion] = useState<BlurRegion>(null);
  const blurAnimationFrameReference = useRef<number | null>(null);

  useEffect(() => {
    messagesReference.current = messages;
  }, [messages]);

  const updateBlurRegion = useCallback(() => {
    const wrapper = wrapperReference.current;
    if (!wrapper) {
      setBlurRegion(null);
      return;
    }

    const visibleMessages = messagesReference.current.filter((message) => filterVisibleMessages(message));
    if (visibleMessages.length === 0) {
      setBlurRegion(null);
      return;
    }

    const wrapperRect = wrapper.getBoundingClientRect();
    let minTop = Number.POSITIVE_INFINITY;
    let maxBottom = Number.NEGATIVE_INFINITY;
    let hasAny = false;

    for (const message of visibleMessages) {
      const element = messageElementsReference.current.get(message.id);
      if (!element) {
        continue;
      }
      const rect = element.getBoundingClientRect();
      minTop = Math.min(minTop, rect.top);
      maxBottom = Math.max(maxBottom, rect.bottom);
      hasAny = true;
    }

    if (!hasAny) {
      setBlurRegion(null);
      return;
    }

    const insetTop = Math.max(0, minTop - wrapperRect.top);
    const insetBottom = Math.max(0, maxBottom - wrapperRect.top);
    const height = Math.max(0, insetBottom - insetTop);
    if (height <= 0) {
      setBlurRegion(null);
      return;
    }

    setBlurRegion((previous) => {
      if (previous && Math.abs(previous.top - insetTop) < 0.5 && Math.abs(previous.height - height) < 0.5) {
        return previous;
      }
      return { top: insetTop, height };
    });
  }, [filterVisibleMessages]);

  const scheduleBlurRegionUpdate = useCallback(() => {
    if (blurAnimationFrameReference.current !== null) {
      cancelWindowAnimationFrame(blurAnimationFrameReference.current);
    }
    const handle = requestWindowAnimationFrame(() => {
      blurAnimationFrameReference.current = null;
      updateBlurRegion();
    });
    if (handle === null) {
      updateBlurRegion();
      return;
    }
    blurAnimationFrameReference.current = handle;
  }, [updateBlurRegion]);

  const registerWrapper = useCallback(
    (node: HTMLDivElement | null) => {
      wrapperReference.current = node;
      scheduleBlurRegionUpdate();
    },
    [scheduleBlurRegionUpdate]
  );

  const registerMessageElement = useCallback(
    (id: string) => (node: HTMLDivElement | null) => {
      const map = messageElementsReference.current;
      if (node) {
        map.set(id, node);
      } else {
        map.delete(id);
      }
      scheduleBlurRegionUpdate();
    },
    [scheduleBlurRegionUpdate]
  );

  useLayoutEffect(() => {
    scheduleBlurRegionUpdate();
  }, [scheduleBlurRegionUpdate]);

  useEffect(() => {
    if (!globalThis.ResizeObserver) {
      return;
    }
    const wrapper = wrapperReference.current;
    if (!wrapper) {
      return;
    }
    const observer = new ResizeObserver(() => {
      scheduleBlurRegionUpdate();
    });
    observer.observe(wrapper);
    return () => {
      observer.disconnect();
    };
  }, [scheduleBlurRegionUpdate]);

  useEffect(
    () => () => {
      if (blurAnimationFrameReference.current !== null) {
        cancelWindowAnimationFrame(blurAnimationFrameReference.current);
        blurAnimationFrameReference.current = null;
      }
    },
    []
  );

  return {
    blurRegion,
    registerWrapper,
    registerMessageElement,
  } satisfies UseChatBlurOverlayResult;
};
